import React from 'react';
import { useForm } from 'react-hook-form';
import { Button, Form } from 'antd';
import { useNavigate, Link, useParams } from 'react-router-dom';
import AuthLayout from './auth-layout';
import {
  FormPassword,
  Space,
  useLoading,
  LoadingState,
} from '@monorepo/react-components';
import { formPasswordValidation } from '@monorepo/types';
import { useStore } from '../../helpers/use-store';
import { useNotification } from '../../hooks/use-notification';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';

interface ResetPasswordFormData {
  password: string;
  confirmPassword: string;
}

const ResetPasswordPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const form = useForm<ResetPasswordFormData>();
  const { loadingState, updateLoadingState } = useLoading(LoadingState.Initial);
  const notificationApi = useNotification();
  const {
    dataStore: { userStore },
  } = useStore();

  const { confirmCode: token } = useParams<{ confirmCode: string }>();

  const onSubmit = async (data: ResetPasswordFormData) => {
    if (!token) {
      notificationApi.error({
        placement: 'top',
        message: t('common.error'),
        description: t('auth.resetPassword.error'),
      });
      return;
    }

    try {
      updateLoadingState(LoadingState.Loading);
      await userStore.resetPassword(token, data.password);

      notificationApi.success({
        placement: 'top',
        message: t('common.success'),
        description: t('auth.resetPassword.success'),
      });

      navigate('/login');
    } catch (e) {
      notificationApi.error({
        placement: 'top',
        message: t('common.error'),
        description: t('auth.resetPassword.error'),
      });
    } finally {
      updateLoadingState(LoadingState.Loaded);
    }
  };

  return (
    <AuthLayout title={t('auth.resetPassword.title')}>
      <Helmet>
        <title>Create New Password | ShortsNinja</title>
        <meta
          name="description"
          content="Set up a new password for your ShortsNinja account. Get back to creating faceless videos with AI voiceover."
        />
        <meta
          name="keywords"
          content="new password, reset password, ShortsNinja, shorts management, faceless videos, ai video automation, ai voiceover, content automation"
        />
      </Helmet>
      <Form onFinish={form.handleSubmit(onSubmit)} layout="vertical">
        <Space size={'large'} direction={'vertical'}>
          <FormPassword
            placeholder={t('auth.resetPassword.newPasswordPlaceholder')}
            form={form}
            controllerProps={{
              name: 'password',
              rules: formPasswordValidation(),
            }}
          />
          <FormPassword
            placeholder={t('auth.resetPassword.confirmPasswordPlaceholder')}
            form={form}
            controllerProps={{
              name: 'confirmPassword',
              rules: {
                ...formPasswordValidation(),
                validate: (value: string) =>
                  value === form.getValues('password') ||
                  t('auth.resetPassword.mismatch'),
              },
            }}
          />
          <Button
            loading={loadingState === LoadingState.Loading}
            type="primary"
            htmlType="submit"
            block
          >
            {t('auth.resetPassword.resetButton')}
          </Button>
        </Space>
      </Form>
      <div className="auth-link">
        <Link to="/login">{t('auth.forgotPassword.backToLogin')}</Link>
      </div>
    </AuthLayout>
  );
};

export default ResetPasswordPage;
